/**
 * scenery/palettes.ts — the bold, saturated low-poly colour vocabulary shared
 * by every city's zone builders. New cities add their own arrays here so the
 * whole world's palette can be art-directed from one place.
 */

// ---- Shared / NYC / London --------------------------------------------------
export const BROWNSTONE = [0x7a4a36, 0x8a553a, 0x6e4632, 0x9a6a48, 0xa6794f, 0x5e4030, 0x8f4a3a];
export const GLASS = [0x4f6188, 0x5d6f93, 0x44557a, 0x6b7da0, 0x7e8eac, 0x9aa6be, 0x586a90, 0x8190ac];
export const SKYLINE = [0x3b4a64, 0x434f6c, 0x515d78];
export const NEON = [0xff2d6a, 0x18d0ff, 0xffd23c, 0xff7a18, 0x4cff8a, 0xff4cf0, 0xffffff, 0x7a5cff];
export const FOLIAGE = [0x2f7a3a, 0x357f3e, 0x468a3a, 0x2a6a34, 0x3c8a46];
export const FOLIAGE_AUTUMN = [0xc97a2a, 0xd0a030, 0xb85a28];
export const GENERIC = [0x6a6f7a, 0x7a7066, 0x5e6470, 0x827a6c, 0x6f7866];
export const CARS = [0x20242e, 0xb23a3a, 0x35508f, 0xe8e8e8, 0x2a7a4a];
export const CAB_YELLOW = 0xf6c513;
export const HILL_GREEN = [0x2f7a36, 0x368a40, 0x2a7036, 0x3f9446];
export const ADBUILDING = [0x14161e, 0x191c25, 0x20232c, 0x101218]; // dark Times Sq towers (the ad is the front)
export const LONDON_STONE = [0xcdbd92, 0xd6c79c, 0xc2b083, 0xb8a878, 0xd9cca6, 0xb0a06a]; // Portland/limestone civic

// ---- Napoli -----------------------------------------------------------------
export const NAPOLI_PASTEL = [0xe07a52, 0xeab35c, 0xdf9d8a, 0xf2e3bc, 0xeacb70, 0xd06a4c, 0xe2ad66];
export const NAPOLI_TUFA = [0xc8b888, 0xb8a878, 0xd0c098, 0xa89868];
export const SCOOTER_COLORS = [0x3aa6c8, 0xd8d2c2, 0xc83a30, 0x2a8a4a, 0xe0a818];
export const BOAT_COLORS = [0xd8563a, 0x3a78b8, 0xe8e2d2, 0x2a9a6a, 0xe0b838];
export const MED_SEA = 0x2a6a8a; // deep Tyrrhenian blue

// ---- Rio --------------------------------------------------------------------
export const RIO_COLONIAL = [0xe8c84a, 0x4aa8d8, 0xe87a9a, 0x8ac86a, 0xe8a04a, 0x9a7ae0, 0xe86a4a];
export const RIO_JUNGLE = [0x1f7a36, 0x2a8a40, 0x18642c, 0x35a04a, 0x2f9a50];
export const RIO_SEA = 0x18b2c8; // turquoise Atlantic
export const RIO_SAND = 0xeede9a;
export const UMBRELLA_COLORS = [0xff5a3c, 0x18b2c8, 0xffd23c, 0x2a9a6a, 0xe84a8a, 0xffffff];

// ---- Dakar ------------------------------------------------------------------
export const DAKAR_RENDER = [0xe8dcc0, 0xd8c8a0, 0xc8b088, 0xe0d0b0, 0xb0a07a, 0xd0bc94];
export const DAKAR_DOORS = [0x2a8a8a, 0xc84a30, 0x3a6ac8, 0x2a9a5a, 0xd8a020];
export const DAKAR_SAND = [0xd8b878, 0xc9a861, 0xe0c088];
export const PINK_LAKE = 0xd87a9a;

// ---- Lagos ------------------------------------------------------------------
export const LAGOS_BLOCKS = [0xd8d2c2, 0xc8b888, 0xe0a818, 0x4aa8d8, 0x2a9a6a, 0xc84a30, 0xe8e2d2];
export const LAGOS_GLASS = [0x4f7a98, 0x5d8aa8, 0x447288, 0x6b9ab8, 0x86b2c8];
export const LAGOS_LAGOON = 0x3a6a72; // warm murky lagoon green
export const DANFO_YELLOW = 0xf2c422;

// ---- Mumbai -----------------------------------------------------------------
export const MUMBAI_GOTHIC = [0x6a675e, 0x76736a, 0x5e5b52, 0x807d72];
export const MUMBAI_DECO = [0xe8dcc8, 0xd8c8b0, 0xe0d0c0, 0xc8b8a8, 0xead8b8];
export const ARABIAN_SEA = 0x4a8a9a; // monsoon-season sea

// ---- Patagonia ----------------------------------------------------------------
export const PATAGONIA_ROOFS = [0xc83a30, 0x3a6ac8, 0x2a8a5a, 0xe0a818];
export const PATAGONIA_WOOD = [0x8a6a4a, 0x9a7a5a, 0xa88a68, 0x7a5e42];
export const LENGA = [0xb83a28, 0xc85a2a, 0xd87a30, 0x4a6a3a, 0x8a6a2a];
export const GLACIAL_LAKE = 0x4ab8c8; // milky turquoise
export const MORAINE = [0x6a7a72, 0x7a8a80, 0x5e6e66];

// ---- Tokyo ------------------------------------------------------------------
export const TOKYO_TIMBER = [0x4a3a30, 0x553f30, 0x3f332a, 0x5e4a38]; // dark temple-town wood
export const TOKYO_NIGHT_GLASS = [0x2e3a54, 0x35425e, 0x283248, 0x3c4868, 0x44506e];
export const SAKURA = [0xe89ab8, 0xdf8aae, 0xeaa8c4]; // cherry blossom

// ---- People (crowds, M36) ---------------------------------------------------
// Nine world cities, so a deliberately DIVERSE set of skin tones. Bright shirts
// read at racing speed; muted trousers ground them. buildCrowd tints one
// InstancedMesh per body part (torso = shirt, head = skin, legs = pants).
export const SKIN_TONES = [0x4a2f1e, 0x5e3d28, 0x7a4f33, 0x95623d, 0xb27c4e, 0xcea074, 0xe7c39a, 0xf2d6b3];
export const SHIRT_COLORS = [
  0xff5a3c, 0x18b2c8, 0xffd23c, 0x2a9a6a, 0xe84a8a, 0xf4f4f4, 0x3a6ac8, 0xe8e8e8,
  0xc83a30, 0x6a3ab0, 0x2a2f3a, 0xf2c422, 0xff7a18, 0x4cb86a, 0xd84a6a,
];
export const PANTS_COLORS = [0x2a3340, 0x3a4150, 0x5a4632, 0x6a6f7a, 0x1c2230, 0x8a6a4a, 0x26262b, 0x445066, 0xb0a890];

/** FINISH FANFARE — the scarves/flags a cheering spectator holds overhead.
 *  Deliberately HOTTER and more saturated than SHIRT_COLORS: a scarf is the
 *  one crowd element meant to read as a colour flash at racing speed, and it
 *  is the only thing in the crowd that is NOT desaturated by desatShirt. */
export const CHEER_PROP_COLORS = [
  0xff3b3b, 0xffd23c, 0x18b2c8, 0x2fd06a, 0xff6a18, 0xf4f4f4, 0xe84a8a, 0x7a5cff, 0xf2b21c,
];

export function darken(hex: number, f: number): number {
  const r = Math.round(((hex >> 16) & 255) * f);
  const g = Math.round(((hex >> 8) & 255) * f);
  const b = Math.round((hex & 255) * f);
  return (r << 16) | (g << 8) | b;
}
